import { config } from '@/config';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { storeExpiresAt } from '@/features/auth/lib/token';
import type { TenantPermissions } from '@/features/auth/lib/api';
import { API_REQUEST_TIMEOUT_MS } from '@/lib/query-constants';

interface ExchangeResponse {
  success: boolean;
  data: {
    token: string;
    refreshToken: string;
    expiresIn: number;
    expiresAt: number;
    csrfToken: string;
    tenants: TenantPermissions[];
    /** The user's access-group IDs. Optional only while an older API pod may answer mid-rollout. */
    accessIds?: string[];
    /** Canonical avatar URL resolved by the backend (uploaded avatar → signed URL, else provider photo). Absent on legacy responses. */
    picture?: string | null;
  };
}

export async function exchangeFirebaseToken(
  firebaseToken: string,
  extra?: { picture?: string | null; name?: string | null },
) {
  const res = await fetch(`${config.authUrl}/auth/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ firebaseToken }),
    signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Exchange failed: ${res.status}`);
  }

  const json = (await res.json()) as ExchangeResponse;
  const data = json.data;

  storeExpiresAt(data.expiresAt);

  // Derive user from the response data (token is now opaque/inaccessible)
  // We parse the token only to get user info — it's still in the response body during transition
  const { parseToken } = await import('@/features/auth/lib/token');
  const payload = parseToken(data.token);
  // Prefer the backend-resolved avatar (uploaded avatar survives re-login); fall
  // back to the Firebase provider photo only when the user has no uploaded avatar.
  const picture = data.picture ?? extra?.picture ?? null;
  const user = payload
    ? { sub: payload.sub, email: payload.email, acs: data.accessIds ?? payload.acs ?? [], name: extra?.name ?? null, picture }
    : { sub: '', email: '', acs: [] as string[], name: null, picture: null };

  useAuth.getState().login({
    user,
    tenants: data.tenants,
    expiresAt: data.expiresAt,
    csrfToken: data.csrfToken,
  });

  return data;
}
